import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { useCallback, useMemo, useState } from "react";
import {
    buildExecutionForest,
    collectAllIds,
    flattenForest,
} from "./executionTree";
import "./ExecutionTree.scss";

interface ExecutionTreeColumn {
    label: string;
    key: string;
    dto: (data: any, refreshAction?: () => void) => any;
    shown?: boolean;
}

interface ExecutionTreeProps {
    /** The flat descendant rows already loaded for the Children tab. */
    data: any[];
    /**
     * The same column definitions the flat list uses (executionsListingColumns).
     * Typed loosely because that config is a heterogeneous union of column shapes.
     */
    columns: any[];
    isLoading?: boolean;
    /** Total descendant count on the server; used to flag when the tree is capped. */
    totalItemCount?: number | null;
}

// Columns rendered in the tree, in order. A tree is meant for browsing structure,
// so it shows a focused, legible subset (the same cell renderers as the flat list)
// rather than every optional column.
const TREE_COLUMN_KEYS = ["name", "status", "relation"];
const INDENT_PX = 20;
const noop = () => undefined;

/**
 * Render the Children-tab descendant set as an expandable/collapsible parent/child
 * tree. The tree is assembled purely from each row's `parent_execution_id` (see
 * ./executionTree). Each node reuses the flat list's cell renderers so a row looks
 * identical to the flat view; the Name cell is indented by depth and carries a
 * caret expand/collapse control. Dependency relations remain visible per row via
 * the shared "Relation" chip column.
 */
export const ExecutionTree = ({
    data,
    columns,
    isLoading,
    totalItemCount,
}: ExecutionTreeProps) => {
    const forest = useMemo(() => buildExecutionForest(data), [data]);
    const allIds = useMemo(() => collectAllIds(forest), [forest]);

    // Track COLLAPSED ids (default: everything expanded). Tracking the collapsed
    // set rather than the expanded set means nodes that arrive later via live
    // updates are shown expanded by default instead of silently hidden.
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());

    const expandedIds = useMemo(() => {
        const expanded = new Set<string>();
        allIds.forEach((id) => {
            if (!collapsedIds.has(id)) {
                expanded.add(id);
            }
        });
        return expanded;
    }, [allIds, collapsedIds]);

    const rows = useMemo(() => flattenForest(forest, expandedIds), [forest, expandedIds]);

    const treeColumns = useMemo(
        () =>
            TREE_COLUMN_KEYS.map((key) => columns.find((column) => column.key === key)).filter(
                (column): column is ExecutionTreeColumn => !!column && column.shown !== false
            ),
        [columns]
    );

    const toggleNode = useCallback((id: string) => {
        setCollapsedIds((previous) => {
            const next = new Set(previous);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
    const collapseAll = useCallback(() => setCollapsedIds(new Set(allIds)), [allIds]);

    const truncated = totalItemCount != null && data.length < totalItemCount;

    return (
        <div className="executionTree">
            <div className="executionTreeControls">
                <Button
                    variation="link"
                    size="small"
                    icon={IconCollection.arrowExpand}
                    label="Expand all"
                    onClick={expandAll}
                />
                <Button
                    variation="link"
                    size="small"
                    icon={IconCollection.arrowCollapse}
                    label="Collapse all"
                    onClick={collapseAll}
                />
            </div>
            <table className="executionTreeTable">
                <thead>
                    <tr>
                        {treeColumns.map((column) => (
                            <th key={column.key}>{column.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const id = row.execution.id;
                        const isExpanded = expandedIds.has(id);
                        return (
                            <tr key={id} className="executionTreeRow">
                                {treeColumns.map((column, index) => {
                                    const cell = column.dto(row.execution, noop);
                                    if (index !== 0) {
                                        return <td key={column.key}>{cell}</td>;
                                    }
                                    return (
                                        <td key={column.key} className="executionTreeNameCell">
                                            <div
                                                className="executionTreeNameInner"
                                                style={{ paddingLeft: row.depth * INDENT_PX }}
                                            >
                                                {row.hasChildren ? (
                                                    <button
                                                        type="button"
                                                        className="executionTreeToggle"
                                                        aria-label={
                                                            isExpanded ? "Collapse" : "Expand"
                                                        }
                                                        onClick={() => toggleNode(id)}
                                                    >
                                                        <Icon
                                                            icon={
                                                                isExpanded
                                                                    ? IconCollection.chevronDown
                                                                    : IconCollection.chevronRight
                                                            }
                                                        />
                                                    </button>
                                                ) : (
                                                    <span className="executionTreeToggle executionTreeToggle--leaf" />
                                                )}
                                                <div className="executionTreeCellContent">
                                                    {cell}
                                                </div>
                                                {row.hasChildren ? (
                                                    <span
                                                        className="executionTreeCount"
                                                        title={`${row.descendantCount} descendant execution(s)`}
                                                    >
                                                        {row.descendantCount}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {isLoading && !rows.length ? (
                <div className="executionTreeLoading">Loading…</div>
            ) : null}
            {!isLoading && !rows.length ? (
                <div className="executionTreeEmpty">No descendant executions.</div>
            ) : null}
            {truncated ? (
                <div className="executionTreeTruncated">
                    Showing the first {data.length} of {totalItemCount} descendants. Narrow the
                    filter to see the rest.
                </div>
            ) : null}
        </div>
    );
};

export default ExecutionTree;
